// ============================================================================
// PARTS4DRIVE MARKETPLACE LIBRARY
// Unified product catalog and search for Parts4Drive
// Calls P4D backend API (not Supabase)
// ============================================================================

const API_BASE_URL = process.env.NEXT_PUBLIC_P4D_URL || 'https://parts4drive.com'

// ============================================================================
// MARKETPLACE OPERATIONS
// ============================================================================

/**
 * Search marketplace for products via the backend catalog API
 */
export async function searchMarketplace(params: {
  partKeyword?: string
  make?: string
  model?: string
  year?: number
  category?: string
  subcategory?: string
  limit?: number
  offset?: number
}) {
  const { partKeyword, make, limit = 10, offset = 0 } = params

  try {
    // Use the backend catalog API
    const response = await fetch(
      `${API_BASE_URL}/api/v1/catalog?keyword=${encodeURIComponent(partKeyword || '')}&limit=${limit}&offset=${offset}`,
      {
        method: 'GET',
        credentials: 'include',
      }
    )

    if (!response.ok) {
      throw new Error(`Catalog search failed: ${response.status}`)
    }

    const data = await response.json()
    const items = data.items || data.listings || data.products || []

    // Map backend format to our expected format
    const results = items.map((item: any) => ({
      id: item.productId || item.id || '',
      name: item.name || item.title || item.partName || '',
      sku: item.sku || item.mpn || '',
      price_minor: item.price_minor || (item.price ? Math.round(item.price * 100) : 0),
      quantity: item.quantity || item.availability || 0,
      make: item.make || item.mmyt_make || '',
      model: item.model || item.mmyt_model || '',
      year: item.year || item.mmyt_year ? parseInt(item.mmyt_year, 10) : undefined,
      category: item.category || item.mmyt_category || '',
      subcategory: item.subcategory || item.mmyt_subcategory || '',
      mmyt_make: item.mmyt_make || item.make || '',
      mmyt_model: item.mmyt_model || item.model || '',
      mmyt_year: item.mmyt_year || item.year || '',
      mmyt_trim: item.mmyt_trim || '',
      mmyt_engine: item.mmyt_engine || '',
      image_url: item.images?.[0] || item.image_url || '',
      seller_name: item.org_name || item.seller_name || '',
    }))

    return {
      results,
      total: data.total || items.length || 0,
      searchParams: params,
    }
  } catch (error) {
    console.error('Marketplace search error:', error)
    return { results: [], total: 0, searchParams: params }
  }
}

/**
 * Get catalog data (MMYTE)
 */
export async function getCatalog() {
  try {
    const response = await fetch(`${API_BASE_URL}/api/v1/catalog`, {
      credentials: 'include',
    })
    return await response.json()
  } catch (error) {
    console.error('Catalog fetch error:', error)
    return { makes: [], models: {}, categories: [] }
  }
}

/**
 * Get product by ID
 */
export async function getProduct(productId: string) {
  try {
    const response = await fetch(`${API_BASE_URL}/api/v1/listings/${productId}`, {
      credentials: 'include',
    })
    return await response.json()
  } catch (error) {
    console.error('Product fetch error:', error)
    return null
  }
}

/**
 * Get user orders via the backend API
 */
export async function getUserOrders(userId: string) {
  try {
    const response = await fetch(`${API_BASE_URL}/api/v1/orders?userId=${encodeURIComponent(userId)}`, {
      credentials: 'include',
    })
    if (!response.ok) {
      // Orders endpoint may not exist yet, return empty
      return { orders: [] }
    }
    return await response.json()
  } catch (error) {
    console.error('Orders fetch error:', error)
    return { orders: [] }
  }
}

// ============================================================================
// TYPES
// ============================================================================

export interface MarketplaceProduct {
  id: string
  name: string
  sku: string
  price_minor: number
  quantity: number
  make?: string
  model?: string
  year?: number
  category?: string
  subcategory?: string
  mmyt_make?: string
  mmyt_model?: string
  mmyt_year?: number
  mmyt_trim?: string
  mmyt_engine?: string
  image_url?: string
  seller_name?: string
}

export interface Order {
  id: string
  user_id: string
  products: MarketplaceProduct[]
  total: number
  total_minor?: number
  status: 'pending' | 'confirmed' | 'shipped' | 'delivered' | 'cancelled'
  shippingAddress?: string
  order_number?: string
  customer_name?: string
  created_at: string
  items?: Array<{ sku: string; name: string; qty: number; price_minor: number }>
}

export interface CatalogResponse {
  makes: Array<{ id: string; name: string }>
  models: Record<string, Array<{ id: string; name: string }>>
  categories: Array<{ id: string; name: string }>
}

// ============================================================================
// EXPORTS
// ============================================================================

export default {
  searchMarketplace,
  getCatalog,
  getProduct,
  getUserOrders,
}
